/**
 * TechApp — Sprint 6.2 (App Técnico / Mobile Installer).
 *
 * App mobile para o técnico em campo. Fluxo guiado de instalação:
 *   login → home → QR → cliente → contrato → template → estoque →
 *   OS → localização → finalizar → sucesso.
 *
 * Mobile-first: botões grandes, etapas claras, modo escuro, cards
 * (sem tabelas grandes), indicador de progresso, feedback visual.
 *
 * Renderizado em /tech (roteamento por pathname no main.jsx), fora
 * do chrome NOC — é uma experiência própria.
 */
import { useEffect, useState } from "react";
import QrScannerModal from "../components/QrScannerModal.jsx";
import InstallPrompt from "../pwa/InstallPrompt.jsx";
import {
  captureLocation,
  finishInstall,
  getTechToken,
  listWorkOrders,
  scanQr,
  searchCustomers,
  selectCustomer,
  setTechToken,
  startSession,
  techPing,
} from "../lib/techApi.js";

// Passos do wizard (após login + home).
const STEPS = [
  "qr", "customer", "contract", "template",
  "inventory", "workorder", "location", "finish",
];
const STEP_LABELS = {
  qr: "QR do case",
  customer: "Cliente IXC",
  contract: "Contrato",
  template: "Template",
  inventory: "Equipamento",
  workorder: "Ordem de Serviço",
  location: "Localização",
  finish: "Finalizar",
};

export default function TechApp() {
  // screen: login | home | wizard | success
  const [screen, setScreen] = useState(
    getTechToken() ? "home" : "login"
  );
  const [installer, setInstaller] = useState(
    () => sessionStorage.getItem("ipwd_tech_installer") || ""
  );
  const [stepIdx, setStepIdx] = useState(0);

  // Estado da sessão em andamento.
  const [session, setSession] = useState(null);
  const [draft, setDraft] = useState({});
  const [result, setResult] = useState(null);

  function resetWizard() {
    setSession(null);
    setDraft({});
    setStepIdx(0);
    setResult(null);
  }

  function handleLogout() {
    setTechToken("");
    sessionStorage.removeItem("ipwd_tech_installer");
    resetWizard();
    setScreen("login");
  }

  return (
    <div className="ipwd-tech min-h-screen bg-noc-bg text-noc-text
                    flex flex-col">
      <TechHeader
        screen={screen}
        installer={installer}
        onLogout={handleLogout}
        onHome={() => { resetWizard(); setScreen("home"); }}
      />

      <main className="flex-1 w-full max-w-md mx-auto px-4 py-4">
        {screen === "login" && (
          <LoginScreen
            onAuthed={(name) => {
              setInstaller(name);
              sessionStorage.setItem("ipwd_tech_installer", name);
              setScreen("home");
            }}
          />
        )}

        {screen === "home" && (
          <HomeScreen
            installer={installer}
            onStart={async () => {
              const s = await startSession(installer);
              setSession(s);
              resetWizardKeepSession(s);
              setScreen("wizard");
            }}
          />
        )}

        {screen === "wizard" && session && (
          <Wizard
            session={session}
            setSession={setSession}
            draft={draft}
            setDraft={setDraft}
            stepIdx={stepIdx}
            setStepIdx={setStepIdx}
            onFinished={(res) => { setResult(res); setScreen("success"); }}
          />
        )}

        {screen === "success" && (
          <SuccessScreen
            result={result}
            onNewInstall={async () => {
              const s = await startSession(installer);
              setSession(s);
              setDraft({});
              setStepIdx(0);
              setResult(null);
              setScreen("wizard");
            }}
            onHome={() => { resetWizard(); setScreen("home"); }}
          />
        )}
      </main>

      {/* Sprint 6.3 — banner "Instalar app" (PWA). */}
      <InstallPrompt />
    </div>
  );

  // Reinicia o wizard mantendo a sessão recém-criada.
  function resetWizardKeepSession(s) {
    setSession(s);
    setDraft({});
    setStepIdx(0);
    setResult(null);
  }
}

/* ════════ Header ════════ */
function TechHeader({ screen, installer, onLogout, onHome }) {
  return (
    <header className="sticky top-0 z-20 bg-noc-panel border-b
                       border-noc-border">
      <div className="max-w-md mx-auto px-4 h-14 flex items-center
                      gap-2">
        <button
          onClick={onHome}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <svg width="26" height="26" viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="7" fill="#15263a" />
            <circle cx="16" cy="16" r="5" fill="none"
                    stroke="#2BD9F0" strokeWidth="2" />
            <circle cx="16" cy="16" r="2" fill="#2BD9F0" />
            <path d="M3 16H8M24 16H29M16 3V8M16 24V29"
                  stroke="#2BD9F0" strokeWidth="2"
                  strokeLinecap="round" />
          </svg>
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">
              App Técnico
            </div>
            <div className="text-[10px] text-noc-muted -mt-0.5 truncate">
              {installer || "Fibra Net"}
            </div>
          </div>
        </button>
        {screen !== "login" && (
          <button
            onClick={onLogout}
            className="text-xs text-noc-muted px-2 py-1 rounded
                       border border-noc-border"
          >
            Sair
          </button>
        )}
      </div>
    </header>
  );
}

/* ════════ 1. Login ════════ */
function LoginScreen({ onAuthed }) {
  const [token, setTokenInput] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit() {
    if (!token.trim() || !name.trim()) {
      setErr("Informe seu nome e o token técnico.");
      return;
    }
    setBusy(true);
    setErr(null);
    setTechToken(token.trim());
    try {
      await techPing();
      onAuthed(name.trim());
    } catch (e) {
      setTechToken("");
      setErr(
        e?.status === 403
          ? "Token técnico inválido."
          : (e?.message || "Falha ao validar o token.")
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pt-8 space-y-4">
      <div className="text-center space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">Acesso do Técnico</h1>
        <p className="text-sm text-noc-muted">
          Entre com seu nome e o token técnico.
        </p>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-noc-muted block mb-1">
            Seu nome
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Lucas"
            className="installer-input"
          />
        </div>
        <div>
          <label className="text-xs text-noc-muted block mb-1">
            Token técnico
          </label>
          <input
            type="password"
            value={token}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="X-Tech-Token"
            className="installer-input"
          />
        </div>
      </div>
      {err && <ErrBox msg={err} />}
      <BigButton onClick={submit} disabled={busy}>
        {busy ? "Validando…" : "Entrar"}
      </BigButton>
    </div>
  );
}

/* ════════ 2. Home ════════ */
function HomeScreen({ installer, onStart }) {
  const [workorders, setWorkorders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    listWorkOrders(installer, { signal: ac.signal })
      .then((d) => setWorkorders(d.work_orders || []))
      .catch(() => setWorkorders([]));
    return () => ac.abort();
  }, [installer]);

  async function start() {
    setBusy(true);
    setErr(null);
    try {
      await onStart();
    } catch (e) {
      setErr(e?.message || "Falha ao iniciar instalação.");
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="glass-card-pad">
        <div className="text-sm font-semibold mb-1">
          Nova instalação
        </div>
        <p className="text-xs text-noc-muted mb-3">
          Inicie o fluxo guiado: QR do case → cliente → finalização.
        </p>
        <BigButton onClick={start} disabled={busy}>
          {busy ? "Iniciando…" : "▶ Iniciar instalação"}
        </BigButton>
        {err && <div className="mt-2"><ErrBox msg={err} /></div>}
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide
                        text-noc-muted mb-2">
          Minhas Ordens de Serviço
        </div>
        {workorders === null && (
          <div className="text-sm font-mono text-[12px] text-noc-faint">
            Carregando…
          </div>
        )}
        {workorders !== null && workorders.length === 0 && (
          <div className="glass-card-pad text-sm text-noc-muted/70">
            Nenhuma OS atribuída a você.
          </div>
        )}
        <div className="space-y-2">
          {(workorders || []).map((wo) => (
            <div key={wo.id}
                 className="glass-card-pad flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {wo.qr_serial || "OS " + String(wo.id).slice(0, 8)}
                </div>
                <div className="text-[11px] text-noc-muted">
                  {wo.location || "—"}
                </div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full
                               border border-noc-border text-noc-muted">
                {wo.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ════════ Wizard — passos 2..10 ════════ */
function Wizard({
  session, setSession, draft, setDraft,
  stepIdx, setStepIdx, onFinished,
}) {
  const step = STEPS[stepIdx];
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  function next() { setStepIdx((i) => Math.min(i + 1, STEPS.length - 1)); }
  function back() { setStepIdx((i) => Math.max(i - 1, 0)); }
  const patch = (p) => setDraft((d) => ({ ...d, ...p }));

  return (
    <div className="space-y-4">
      <ProgressBar stepIdx={stepIdx} />
      <div className="text-sm font-semibold">
        {stepIdx + 1}. {STEP_LABELS[step]}
      </div>

      {err && <ErrBox msg={err} />}

      {step === "qr" && (
        <StepQr
          session={session} setSession={setSession}
          onNext={next} setErr={setErr}
        />
      )}
      {step === "customer" && (
        <StepCustomer
          draft={draft} patch={patch}
          onNext={next} onBack={back} setErr={setErr}
        />
      )}
      {step === "contract" && (
        <StepContract
          draft={draft} patch={patch}
          onNext={next} onBack={back}
        />
      )}
      {step === "template" && (
        <StepTemplate
          session={session} draft={draft} patch={patch}
          onNext={next} onBack={back} setErr={setErr}
        />
      )}
      {step === "inventory" && (
        <StepInventory
          session={session} onNext={next} onBack={back}
        />
      )}
      {step === "workorder" && (
        <StepWorkOrder
          draft={draft} patch={patch}
          onNext={next} onBack={back}
        />
      )}
      {step === "location" && (
        <StepLocation
          session={session} setSession={setSession}
          onNext={next} onBack={back} setErr={setErr}
        />
      )}
      {step === "finish" && (
        <StepFinish
          session={session} draft={draft}
          busy={busy} setBusy={setBusy}
          onBack={back} setErr={setErr}
          onFinished={onFinished}
        />
      )}
    </div>
  );
}

/* ──── Indicador de progresso ──── */
function ProgressBar({ stepIdx }) {
  const pct = ((stepIdx + 1) / STEPS.length) * 100;
  return (
    <div>
      <div className="flex justify-between text-[10px] text-noc-muted
                      mb-1">
        <span>Passo {stepIdx + 1} de {STEPS.length}</span>
        <span>{Math.round(pct)}%</span>
      </div>
      <div className="h-2 rounded-full bg-noc-panel2 overflow-hidden">
        <div
          className="h-full bg-noc-accent transition-all duration-300"
          style={{ width: pct + "%" }}
        />
      </div>
    </div>
  );
}

/* ──── Passo: QR ──── */
function StepQr({ session, setSession, onNext, setErr }) {
  const [scanner, setScanner] = useState(false);
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);

  async function commit(qr) {
    if (!qr || !qr.trim()) {
      setErr("Informe ou escaneie o QR do case.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const s = await scanQr(session.id, qr.trim());
      setSession(s);
      onNext();
    } catch (e) {
      setErr(e?.message || "Falha ao registrar o QR.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-noc-muted">
        Escaneie o QR do case do equipamento ou digite o código.
      </p>
      <BigButton onClick={() => setScanner(true)}>
        📷 Escanear QR
      </BigButton>
      <div className="text-center text-xs text-noc-muted">ou</div>
      <input
        value={manual}
        onChange={(e) => setManual(e.target.value)}
        placeholder="Digite o QR manualmente"
        className="installer-input"
      />
      <BigButton
        variant="ghost"
        onClick={() => commit(manual)}
        disabled={busy}
      >
        {busy ? "Registrando…" : "Confirmar QR digitado"}
      </BigButton>

      {scanner && (
        <QrScannerModal
          onDetected={(qr) => { setScanner(false); commit(qr); }}
          onManual={(qr) => { setScanner(false); setManual(qr); }}
          onClose={() => setScanner(false)}
        />
      )}
    </div>
  );
}

/**
 * Hotfix S6.2D — mapeia o `source` do backend para o badge visual.
 *   customer_id    — termo casou o ID do cliente
 *   pppoe_login_id — termo casou o ID do login PPPoE
 *   pppoe_login    — termo casou o login PPPoE textual
 *   customer       — casou por nome / CPF-CNPJ
 */
function badgeForSource(source) {
  switch (source) {
    case "pppoe_login":
      return {
        label: "Login PPPoE",
        cls: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
      };
    case "pppoe_login_id":
      return {
        label: "ID do login",
        cls: "text-noc-purple bg-noc-purple/10 border-noc-purple/30",
      };
    case "customer_id":
      return {
        label: "ID cliente",
        cls: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
      };
    default:
      return {
        label: "Cliente",
        cls: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
      };
  }
}

/* ──── Passo: Cliente ──── */
function StepCustomer({ draft, patch, onNext, onBack, setErr }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  // Índice do card selecionado — identifica o card de forma única,
  // mesmo quando dois resultados compartilham o customer_id (ex.:
  // cliente achado por nome E por login PPPoE).
  const [selIdx, setSelIdx] = useState(
    draft._customer_card_idx ?? null
  );

  async function search() {
    if (q.trim().length < 1) return;
    setBusy(true);
    setErr(null);
    setSelIdx(null);
    try {
      const d = await searchCustomers(q.trim());
      setResults(d.customers || []);
    } catch (e) {
      setErr(e?.message || "Falha na busca de clientes.");
      setResults([]);
    } finally {
      setBusy(false);
    }
  }

  function selectCard(idx, c) {
    setSelIdx(idx);
    patch({
      customer_id: c.id,
      customer_name: c.name,
      customer_city: c.city,
      // Resultado via login PPPoE traz contrato + login vinculados.
      // Preserva tudo para os próximos passos.
      contract_id: c.contract_id ?? draft.contract_id ?? null,
      contract_name: c.contract_name ?? null,
      pppoe_login: c.pppoe_login ?? null,
      login_id: c.login_id ?? null,
      _customer_card_idx: idx,
    });
  }

  const list = results || [];
  // Aviso de ambiguidade: o termo é numérico e voltou mais de um
  // resultado (tipicamente ID de cliente + login PPPoE com aquele id).
  const numericTerm = q.trim().length > 0 && /^\d+$/.test(q.trim());
  const showAmbiguityHint = numericTerm && list.length > 1;

  return (
    <div className="space-y-3">
      <p className="text-xs text-noc-muted">
        Busque por nome, CPF/CNPJ, ID cliente ou login PPPoE.
      </p>
      <div className="flex gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Nome, CPF/CNPJ, ID, login PPPoE…"
          className="installer-input flex-1"
        />
        <button
          onClick={search}
          disabled={busy}
          className="px-4 rounded-lg bg-noc-accent/15 text-noc-accent
                     border border-noc-accent/30 text-sm font-medium"
        >
          {busy ? "…" : "Buscar"}
        </button>
      </div>

      {/* Aviso de resultado ambíguo (ID cliente + login PPPoE) */}
      {showAmbiguityHint && (
        <div className="text-[11px] text-noc-warn bg-noc-warn/10
                        border border-noc-warn/30 rounded-lg p-2">
          Encontramos cliente pelo ID e também por login PPPoE.
          Selecione o correto.
        </div>
      )}

      <div className="space-y-2">
        {results !== null && list.length === 0 && (
          <div className="text-sm font-mono text-[12px] text-noc-faint">
            Nenhum cliente encontrado.
          </div>
        )}
        {results !== null && list.length > 0 && (
          <div className="text-[10px] text-noc-muted/70">
            {list.length}{" "}
            {list.length === 1 ? "resultado" : "resultados"}
          </div>
        )}
        {list.map((c, idx) => {
          const sel = selIdx === idx;
          // Hotfix S6.2D — o badge usa o source REAL devolvido pelo
          // backend, não a mera presença de pppoe_login.
          const badge = badgeForSource(c.source);
          // mostra o bloco de detalhes do login se há login PPPoE
          const hasLogin = !!c.pppoe_login || c.login_id != null;
          return (
            <button
              key={idx + "-" + c.id + "-" + (c.login_id || "0")}
              onClick={() => selectCard(idx, c)}
              className={
                "w-full text-left glass-card-pad transition-colors "
                + "relative border "
                + (sel
                    ? "border-noc-accent bg-noc-accent/10 "
                      + "ring-1 ring-noc-accent/40"
                    : "border-noc-border hover:border-noc-accent/40")
              }
            >
              {/* marca de selecionado */}
              {sel && (
                <span className="absolute top-2 right-2 text-noc-accent
                                 text-sm">
                  ✓
                </span>
              )}

              {/* Badge de origem — derivado do source real */}
              <div className="mb-1">
                <span className={
                  "text-[10px] font-semibold rounded px-1.5 py-0.5 "
                  + "border " + badge.cls
                }>
                  {badge.label}
                </span>
              </div>

              <div className="text-sm font-medium pr-5">{c.name}</div>
              <div className="text-[11px] text-noc-muted">
                ID cliente {c.id}
                {c.document ? " · " + c.document : ""}
                {c.city ? " · " + c.city : ""}
              </div>

              {/* Detalhes do login PPPoE */}
              {hasLogin && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1">
                  {c.pppoe_login && (
                    <span className="inline-flex items-center gap-1
                                     text-[10px] text-noc-accent
                                     bg-noc-accent/10 border
                                     border-noc-accent/30 rounded
                                     px-1.5 py-0.5">
                      🔑 {c.pppoe_login}
                    </span>
                  )}
                  {c.login_id != null && (
                    <span className="text-[10px] text-noc-muted
                                     border border-noc-border rounded
                                     px-1.5 py-0.5">
                      login_id {c.login_id}
                    </span>
                  )}
                  {c.contract_id && (
                    <span className="text-[10px] text-noc-muted
                                     border border-noc-border rounded
                                     px-1.5 py-0.5">
                      contrato {c.contract_id}
                      {c.contract_name ? " · " + c.contract_name : ""}
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <NavRow
        onBack={onBack}
        onNext={onNext}
        nextDisabled={selIdx === null}
      />
    </div>
  );
}

/* ──── Passo: Contrato ──── */
function StepContract({ draft, patch, onNext, onBack }) {
  // O IXC pode ter múltiplos contratos; aqui o técnico informa o
  // número do contrato (ou deixa em branco para o backend resolver).
  const [contract, setContract] = useState(
    draft.contract_id ? String(draft.contract_id) : ""
  );
  // O contrato veio pré-preenchido pelo login PPPoE selecionado.
  const prefilled = !!draft.contract_id && !!draft.pppoe_login;

  return (
    <div className="space-y-3">
      <div className="glass-card-pad">
        <div className="text-xs text-noc-muted">Cliente</div>
        <div className="text-sm font-medium">
          {draft.customer_name || "—"}
        </div>
        <div className="text-[11px] text-noc-muted">
          ID cliente {draft.customer_id || "—"}
        </div>
        {draft.pppoe_login && (
          <div className="mt-1 text-[11px] text-noc-accent">
            🔑 Login PPPoE: {draft.pppoe_login}
            {draft.login_id != null
              ? "  (login_id " + draft.login_id + ")" : ""}
          </div>
        )}
      </div>

      {prefilled && (
        <div className="text-[11px] text-noc-ok bg-noc-ok/10
                        border border-noc-ok/30 rounded-lg p-2">
          ✓ Contrato {draft.contract_id}
          {draft.contract_name ? " · " + draft.contract_name : ""}{" "}
          já vinculado pelo login PPPoE. Pode avançar.
        </div>
      )}

      <p className="text-xs text-noc-muted">
        {prefilled
          ? "Você pode ajustar o número do contrato se necessário."
          : "Informe o número do contrato do cliente. Se houver só "
            + "um, pode deixar em branco."}
      </p>
      <input
        value={contract}
        onChange={(e) => setContract(e.target.value)}
        placeholder="Nº do contrato (opcional)"
        inputMode="numeric"
        className="installer-input"
      />
      <NavRow
        onBack={onBack}
        onNext={() => {
          patch({
            contract_id: contract.trim()
              ? Number(contract.trim()) : null,
          });
          onNext();
        }}
      />
    </div>
  );
}

/* ──── Passo: Template (sugerido) ──── */
function StepTemplate({
  session, draft, patch, onNext, onBack, setErr,
}) {
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const ac = new AbortController();
    setBusy(true);
    selectCustomer({
      session_id: session.id,
      customer_id: draft.customer_id,
      contract_id: draft.contract_id || null,
      city: draft.customer_city || null,
    }, { signal: ac.signal })
      .then((sug) => {
        setSuggestion(sug);
        if (sug?.suggested_template_id) {
          patch({ template_id: sug.suggested_template_id });
        }
      })
      .catch((e) => {
        if (e?.name !== "AbortError") {
          setErr(e?.message || "Falha ao sugerir template.");
        }
        setSuggestion({ suggested_template_id: null });
      })
      .finally(() => setBusy(false));
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3">
      <p className="text-xs text-noc-muted">
        O sistema sugere um template a partir do perfil do cliente.
      </p>
      {busy && (
        <div className="glass-card-pad text-sm text-noc-muted/70">
          Calculando sugestão…
        </div>
      )}
      {!busy && suggestion && (
        <div className="glass-card-pad space-y-1">
          {suggestion.suggested_template_id ? (
            <>
              <div className="text-sm font-medium text-noc-accent">
                ✓ Template sugerido
              </div>
              {suggestion.profile && (
                <div className="text-xs text-noc-muted">
                  Perfil: {suggestion.profile}
                </div>
              )}
              {suggestion.reason && (
                <div className="text-[11px] text-noc-muted/80">
                  {suggestion.reason}
                </div>
              )}
              <div className="text-[10px] text-noc-muted/60 font-mono">
                {String(suggestion.suggested_template_id).slice(0, 18)}…
              </div>
            </>
          ) : (
            <div className="text-sm text-noc-muted">
              Nenhuma regra casou — a OS será criada sem template
              automático. Tudo bem prosseguir.
            </div>
          )}
        </div>
      )}
      <NavRow onBack={onBack} onNext={onNext} nextDisabled={busy} />
    </div>
  );
}

/* ──── Passo: Equipamento/Estoque ──── */
function StepInventory({ session, onNext, onBack }) {
  return (
    <div className="space-y-3">
      <div className="glass-card-pad space-y-1">
        <div className="text-xs text-noc-muted">QR do case</div>
        <div className="text-sm font-mono font-medium">
          {session.qr_serial || "—"}
        </div>
      </div>
      <p className="text-xs text-noc-muted">
        Confirme que o equipamento em mãos corresponde a este QR.
        A reserva no estoque é feita ao finalizar.
      </p>
      <div className="glass-card-pad flex items-center gap-2">
        <span className="text-noc-accent">📦</span>
        <span className="text-sm">
          Equipamento confirmado para reserva.
        </span>
      </div>
      <NavRow onBack={onBack} onNext={onNext} />
    </div>
  );
}

/* ──── Passo: Ordem de Serviço ──── */
function StepWorkOrder({ draft, patch, onNext, onBack }) {
  const mode = draft.wo_mode || "create";
  return (
    <div className="space-y-3">
      <p className="text-xs text-noc-muted">
        Vincule a uma OS existente ou crie uma nova para esta
        instalação.
      </p>
      <button
        onClick={() => patch({ wo_mode: "create", wo_id: null })}
        className={
          "w-full text-left glass-card-pad transition-colors "
          + (mode === "create"
              ? "border-noc-accent/60 bg-noc-accent/10" : "")
        }
      >
        <div className="text-sm font-medium">＋ Criar nova OS</div>
        <div className="text-[11px] text-noc-muted">
          Gera uma OS nova ao finalizar a instalação.
        </div>
      </button>
      <button
        onClick={() => patch({ wo_mode: "existing" })}
        className={
          "w-full text-left glass-card-pad transition-colors "
          + (mode === "existing"
              ? "border-noc-accent/60 bg-noc-accent/10" : "")
        }
      >
        <div className="text-sm font-medium">🔗 Vincular OS existente</div>
        <div className="text-[11px] text-noc-muted">
          Informe o ID da OS já aberta para este cliente.
        </div>
      </button>
      {mode === "existing" && (
        <input
          value={draft.wo_id || ""}
          onChange={(e) => patch({ wo_id: e.target.value })}
          placeholder="ID da OS"
          className="installer-input"
        />
      )}
      <NavRow onBack={onBack} onNext={onNext} />
    </div>
  );
}

/* ──── Passo: Localização ──── */
function StepLocation({
  session, setSession, onNext, onBack, setErr,
}) {
  const [loc, setLoc] = useState(
    session.location || null
  );
  const [busy, setBusy] = useState(false);

  function capture() {
    if (!navigator.geolocation) {
      setErr("GPS não disponível neste aparelho.");
      return;
    }
    setBusy(true);
    setErr(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const payload = {
          session_id: session.id,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        };
        try {
          const s = await captureLocation(payload);
          setSession(s);
          setLoc(s.location);
        } catch (e) {
          setErr(e?.message || "Falha ao salvar a localização.");
        } finally {
          setBusy(false);
        }
      },
      (geoErr) => {
        // Sprint 6.3 — mensagens claras por tipo de erro mobile.
        let msg = "Não foi possível obter o GPS.";
        if (geoErr.code === 1) {
          msg = "Permissão de localização negada. Autorize o "
              + "acesso ao GPS nas configurações do navegador.";
        } else if (geoErr.code === 2) {
          msg = "Localização indisponível. Verifique se o GPS do "
              + "aparelho está ligado.";
        } else if (geoErr.code === 3) {
          msg = "Tempo esgotado ao obter o GPS. Tente novamente "
              + "em local mais aberto.";
        }
        setErr(msg);
        setBusy(false);
      },
      // Sprint 6.3 — alta precisão, sem cache (posição fresca).
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }

  // Precisão acima de ~50 m é fraca para marcar um ponto de
  // instalação — avisa o técnico para tentar de novo.
  const lowAccuracy = loc && loc.accuracy && loc.accuracy > 50;

  return (
    <div className="space-y-3">
      <p className="text-xs text-noc-muted">
        Capture a localização do ponto de instalação pelo GPS do
        celular.
      </p>
      <BigButton onClick={capture} disabled={busy}>
        {busy
          ? "Obtendo GPS…"
          : (loc ? "📍 Capturar de novo" : "📍 Capturar localização")}
      </BigButton>
      {loc && (
        <div className="glass-card-pad space-y-0.5">
          <div className="text-sm font-medium text-noc-accent">
            ✓ Localização capturada
          </div>
          <div className="text-[11px] text-noc-muted font-mono">
            {Number(loc.lat).toFixed(6)}, {Number(loc.lng).toFixed(6)}
          </div>
          {loc.accuracy && (
            <div className="text-[10px] text-noc-muted/70">
              precisão ~{Math.round(loc.accuracy)} m
            </div>
          )}
          {lowAccuracy && (
            <div className="text-[11px] text-noc-warn mt-1">
              Precisão baixa. Se possível, vá a um local mais
              aberto e capture de novo.
            </div>
          )}
        </div>
      )}
      <NavRow
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!loc}
      />
    </div>
  );
}

/* ──── Passo: Finalizar ──── */
function StepFinish({
  session, draft, busy, setBusy, onBack, setErr, onFinished,
}) {
  async function finish() {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        session_id: session.id,
        template_id: draft.template_id || null,
        notes: draft.notes || null,
      };
      if (draft.wo_mode === "existing" && draft.wo_id) {
        payload.work_order_id = draft.wo_id;
      } else {
        payload.create_work_order = true;
      }
      const res = await finishInstall(payload);
      onFinished(res);
    } catch (e) {
      setErr(e?.message || "Falha ao finalizar a instalação.");
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="glass-card-pad space-y-2">
        <div className="text-sm font-semibold">Resumo</div>
        <Row k="QR do case" v={session.qr_serial || "—"} />
        <Row k="Cliente" v={draft.customer_name || "—"} />
        <Row k="Contrato"
             v={draft.contract_id ? String(draft.contract_id)
                                  : "automático"} />
        <Row k="Template"
             v={draft.template_id ? "sugerido" : "sem template"} />
        <Row k="OS"
             v={draft.wo_mode === "existing"
                ? "vincular existente" : "criar nova"} />
        <Row k="Localização"
             v={session.location ? "capturada" : "—"} />
      </div>
      <p className="text-[11px] text-noc-muted">
        Ao finalizar, o estoque é reservado e a OS atualizada. O
        device será vinculado automaticamente quando enviar o
        primeiro heartbeat.
      </p>
      <BigButton onClick={finish} disabled={busy}>
        {busy ? "Finalizando…" : "✓ Finalizar instalação"}
      </BigButton>
      <BigButton variant="ghost" onClick={onBack} disabled={busy}>
        ← Voltar
      </BigButton>
    </div>
  );
}

/* ════════ Tela de sucesso ════════ */
function SuccessScreen({ result, onNewInstall, onHome }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="pt-8 space-y-4 text-center">
      <div className="text-5xl">✅</div>
      <h1 className="text-2xl font-bold tracking-tight">Instalação concluída!</h1>
      <p className="text-sm text-noc-muted">
        {result?.message
          || "Estoque e OS prontos. O auto-bind concluirá no "
             + "primeiro heartbeat."}
      </p>
      {result && (
        <div className="glass-card-pad text-left space-y-1">
          {result.inventory_status && (
            <Row k="Estoque" v={result.inventory_status} />
          )}
          {result.work_order_id && (
            <Row k="OS"
                 v={String(result.work_order_id).slice(0, 8) + "…"} />
          )}
          {result.session?.qr_serial && (
            <Row k="QR" v={result.session.qr_serial} />
          )}
        </div>
      )}
      <BigButton
        onClick={async () => {
          setBusy(true);
          try { await onNewInstall(); }
          finally { setBusy(false); }
        }}
        disabled={busy}
      >
        ＋ Nova instalação
      </BigButton>
      <BigButton variant="ghost" onClick={onHome}>
        Voltar ao início
      </BigButton>
    </div>
  );
}

/* ════════ Componentes auxiliares ════════ */
function BigButton({ children, onClick, disabled, variant = "primary" }) {
  const cls = variant === "ghost"
    ? "border border-noc-border text-noc-text bg-transparent"
    : "bg-noc-accent/15 text-noc-accent border border-noc-accent/40";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={
        "w-full h-12 rounded-xl text-sm font-semibold "
        + "transition-colors disabled:opacity-50 " + cls
      }
    >
      {children}
    </button>
  );
}

function NavRow({ onBack, onNext, nextDisabled }) {
  return (
    <div className="flex gap-2 pt-1">
      <button
        onClick={onBack}
        className="flex-1 h-11 rounded-xl text-sm font-medium
                   border border-noc-border text-noc-muted"
      >
        ← Voltar
      </button>
      <button
        onClick={onNext}
        disabled={nextDisabled}
        className="flex-1 h-11 rounded-xl text-sm font-semibold
                   bg-noc-accent/15 text-noc-accent
                   border border-noc-accent/40 disabled:opacity-50"
      >
        Avançar →
      </button>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-3 text-xs">
      <span className="text-noc-muted">{k}</span>
      <span className="text-noc-text font-medium text-right truncate">
        {v}
      </span>
    </div>
  );
}

function ErrBox({ msg }) {
  return (
    <div className="text-xs text-noc-err bg-noc-err/10 border
                    border-noc-err/30 rounded-lg p-2.5">
      {msg}
    </div>
  );
}
